import { useEffect, useMemo, useState, useCallback, useRef } from "react"
import { Card, Button, Space, Select, Drawer } from "antd"
import {
  FilterOutlined,
  SearchOutlined,
  PlusOutlined,
} from "@ant-design/icons"
import { Toast } from "@/components/Toast"
import TrainingPlanTable from "../training-plan/components/TrainingPlanTable"
import TrainingRecordTable from "./components/TrainingRecordTable"
import TrainingRecordModal from "./components/TrainingRecordModal"
import { useCrud } from "@/hooks/useCrud"
import {
  TrainingPlan,
  TrainingPlanFormData,
  TrainingPlanQueryParams,
  TrainingPlanStatus,
} from "../training-plan/types"
import trainingPlanApi from "../training-plan/api"
import trainingRecordApi from "./api"
import domainLv1Api from "../../domain/domain-lv1/api"
import userApi from "../../system/user/api"
import { User } from "../../system/user/types"
import studentApi from "../../people/student/api"
import { Student } from "../../people/student/types"
import domainLv2Api from "../../domain/domain-lv2/api"
import { DomainLv2 } from "../../domain/domain-lv2/types"
import { useDomainLv1 } from "@/hooks/useDomainLv1"
import { TrainingRecord, TrainingRecordFormData } from "./types"
import { ID } from "@/types/common"

const { Option } = Select

const TrainingPlanRecordManagement = () => {
  const [selectedStudentId, setSelectedStudentId] = useState<ID | undefined>(
    undefined,
  )
  const [selectedStatus, setSelectedStatus] = useState<
    TrainingPlanStatus | undefined
  >(undefined)
  const [selectedDomainLv1Id, setSelectedDomainLv1Id] = useState<
    ID | undefined
  >(undefined)
  const [selectedDomainLv2Id, setSelectedDomainLv2Id] = useState<
    ID | undefined
  >(undefined)
  const [students, setStudents] = useState<Student[]>([])
  const [studentsLoading, setStudentsLoading] = useState(false)
  const [domainLv2s, setDomainLv2s] = useState<DomainLv2[]>([])
  const [domainLv2sLoading, setDomainLv2sLoading] = useState(false)
  const [assistTypes, setAssistTypes] = useState<
    Array<{ id: ID; name: string }>
  >([])
  const [assistTypesLoading, setAssistTypesLoading] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [usersLoading, setUsersLoading] = useState(false)

  const [recordDrawerOpen, setRecordDrawerOpen] = useState(false)
  const [activePlan, setActivePlan] = useState<TrainingPlan | null>(null)
  const [recordLoading, setRecordLoading] = useState(false)
  const [records, setRecords] = useState<TrainingRecord[]>([])
  const [recordModalOpen, setRecordModalOpen] = useState(false)
  const [recordOpLoading, setRecordOpLoading] = useState(false)
  const [recordPagination, setRecordPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
  })
  const { domainLv1s, isLoading: domainLv1sLoading } = useDomainLv1()

  const loadAssistTypesByDomain = useCallback(async (domainLv1Id?: ID) => {
    if (!domainLv1Id) {
      setAssistTypes([])
      return
    }
    try {
      setAssistTypesLoading(true)
      const boundAssistTypes =
        await domainLv1Api.getBoundAssistTypes(domainLv1Id)
      setAssistTypes(
        boundAssistTypes
          .filter((item) => item.enabled)
          .map((item) => ({
            id: item.id,
            name: item.label,
          }))
      )
    } catch (error) {
      Toast.error("加载辅助类型列表失败")
      console.error("加载辅助类型列表失败:", error)
    } finally {
      setAssistTypesLoading(false)
    }
  }, [])

  useEffect(() => {
    const loadBaseData = async () => {
      try {
        setStudentsLoading(true)
        setUsersLoading(true)
        const [studentRes, userRes] = await Promise.all([
          studentApi.getList(),
          userApi.getList(),
        ])
        setStudents(studentRes.data || [])
        setUsers(userRes.data || [])
      } catch (error) {
        Toast.error("加载学生列表失败")
        console.error("加载学生列表失败:", error)
      } finally {
        setStudentsLoading(false)
        setUsersLoading(false)
      }
    }
    loadBaseData()
  }, [])

  useEffect(() => {
    const loadDomainLv2s = async () => {
      if (!selectedDomainLv1Id) {
        setDomainLv2s([])
        return
      }
      try {
        setDomainLv2sLoading(true)
        const res = await domainLv2Api.getList(selectedDomainLv1Id)
        setDomainLv2s(res.data || [])
      } catch (error) {
        Toast.error("加载二级领域列表失败")
        console.error("加载二级领域列表失败:", error)
      } finally {
        setDomainLv2sLoading(false)
      }
    }

    loadDomainLv2s()
    loadAssistTypesByDomain(selectedDomainLv1Id)
    setSelectedDomainLv2Id(undefined)
  }, [selectedDomainLv1Id, loadAssistTypesByDomain])

  const api = useMemo(() => {
    const queryParams: TrainingPlanQueryParams = {
      studentId: selectedStudentId,
      status: selectedStatus,
      domainLv1Id: selectedDomainLv1Id,
      domainLv2Id: selectedDomainLv2Id,
    }

    return {
      getList: () => trainingPlanApi.getList(queryParams),
      getById: trainingPlanApi.getById,
      create: trainingPlanApi.create,
      update: (
        id: ID,
        data: TrainingPlanFormData | Partial<TrainingPlanFormData>,
      ) => trainingPlanApi.update(id, data as TrainingPlanFormData),
      delete: trainingPlanApi.delete,
    }
  }, [
    selectedStudentId,
    selectedStatus,
    selectedDomainLv1Id,
    selectedDomainLv2Id,
  ])

  const {
    items: trainingPlans,
    loading,
    loadItems,
  } = useCrud<TrainingPlan, TrainingPlanFormData, ID>({
    api,
    entityName: "训练计划",
    autoLoad: false,
  })

  const loadItemsRef = useRef(loadItems)

  useEffect(() => {
    loadItemsRef.current = loadItems
  }, [loadItems])

  const handleStudentFilter = (studentId: ID | undefined) => {
    setSelectedStudentId(studentId)
  }

  const handleStatusFilter = (status: TrainingPlanStatus | undefined) => {
    setSelectedStatus(status)
  }

  const handleDomainLv1Filter = (domainLv1Id: ID | undefined) => {
    setSelectedDomainLv1Id(domainLv1Id)
  }

  const handleDomainLv2Filter = (domainLv2Id: ID | undefined) => {
    setSelectedDomainLv2Id(domainLv2Id)
  }

  useEffect(() => {
    if (!selectedStudentId || !selectedDomainLv1Id) {
      return
    }
    loadItemsRef.current()
  }, [selectedStatus, selectedDomainLv2Id])

  const handleSearch = () => {
    loadItems()
  }

  const loadRecords = async (
    trainingPlanId: ID,
    page = 1,
    pageSize = 10,
  ) => {
    try {
      setRecordLoading(true)
      const res = await trainingRecordApi.getList({
        trainingPlanId,
        page,
        pageSize,
      })
      setRecords(res.data || [])
      setRecordPagination({
        page: res.page ?? page,
        pageSize: res.pageSize ?? pageSize,
        total: res.total ?? 0,
      })
    } catch (error) {
      Toast.error("加载训练记录失败")
      console.error("加载训练记录失败:", error)
    } finally {
      setRecordLoading(false)
    }
  }

  const handleViewRecords = (plan: TrainingPlan) => {
    setActivePlan(plan)
    setRecordDrawerOpen(true)
    if (plan.trainingProjectDomainLv1Id) {
      loadAssistTypesByDomain(plan.trainingProjectDomainLv1Id)
    }
    loadRecords(plan.id, 1, recordPagination.pageSize)
  }

  const handleRecordPageChange = (page: number, pageSize: number) => {
    if (!activePlan) return
    loadRecords(activePlan.id, page, pageSize)
  }

  const handleCloseDrawer = () => {
    setRecordDrawerOpen(false)
    setActivePlan(null)
    setRecords([])
    setRecordPagination({ page: 1, pageSize: 10, total: 0 })
    setRecordModalOpen(false)
  }

  const handleOpenRecordModal = (plan?: TrainingPlan) => {
    const resolvedPlan = plan ?? activePlan
    if (!resolvedPlan) {
      Toast.warning("请先选择训练计划")
      return
    }
    setActivePlan(resolvedPlan)
    if (resolvedPlan.trainingProjectDomainLv1Id) {
      loadAssistTypesByDomain(resolvedPlan.trainingProjectDomainLv1Id)
    }
    setRecordModalOpen(true)
  }

  const handleCloseRecordModal = () => {
    setRecordModalOpen(false)
  }

  const handleSaveRecord = async (data: TrainingRecordFormData) => {
    if (!activePlan) return
    try {
      setRecordOpLoading(true)
      await trainingRecordApi.create(data)
      Toast.success("训练记录创建成功")
      setRecordModalOpen(false)
      loadRecords(activePlan.id, 1, recordPagination.pageSize)
      loadItems()
    } catch (error) {
      Toast.error("创建训练记录失败")
      console.error("创建训练记录失败:", error)
    } finally {
      setRecordOpLoading(false)
    }
  }
  
  const statusOptions = [
    { label: "未开始", value: TrainingPlanStatus.NOT_STARTED },
    { label: "进行中", value: TrainingPlanStatus.IN_PROGRESS },
    { label: "已完成", value: TrainingPlanStatus.COMPLETED },
    { label: "已暂停", value: TrainingPlanStatus.SUSPENDED },
  ]

  const drawerTitle = activePlan
    ? `训练计划记录 - ${activePlan.studentName || ""} ${
        activePlan.trainingProjectName || ""
      }`.trim()
    : "训练计划记录"

  return (
    <div className="p-6">
      <Card>
        <div className="flex justify-between items-center mb-4">
          <Space>
            <div className="flex items-center gap-2">
              <FilterOutlined className="text-gray-500" />
              <span className="text-sm text-gray-600">学生：</span>
              <Select
                placeholder="请选择学生"
                style={{ width: 200 }}
                value={selectedStudentId}
                onChange={handleStudentFilter}
                loading={studentsLoading}
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {students.map((student) => (
                  <Option key={student.id} value={student.id}>
                    {student.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">一级领域：</span>
              <Select
                placeholder="请选择一级领域"
                style={{ width: 180 }}
                value={selectedDomainLv1Id}
                onChange={handleDomainLv1Filter}
                loading={domainLv1sLoading}
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {domainLv1s.map((domain) => (
                  <Option key={domain.id} value={domain.id}>
                    {domain.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">二级领域：</span>
              <Select
                placeholder="请选择二级领域"
                style={{ width: 180 }}
                value={selectedDomainLv2Id}
                onChange={handleDomainLv2Filter}
                loading={domainLv2sLoading}
                allowClear
                disabled={!selectedDomainLv1Id}
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    ?.toLowerCase()
                    ?.includes(input.toLowerCase())
                }
              >
                {domainLv2s.map((domain) => (
                  <Option key={domain.id} value={domain.id}>
                    {domain.name}
                  </Option>
                ))}
              </Select>
              <span className="text-sm text-gray-600">状态：</span>
              <Select
                placeholder="请选择状态"
                style={{ width: 180 }}
                value={selectedStatus}
                onChange={handleStatusFilter}
                allowClear
              >
                {statusOptions.map((option) => (
                  <Option key={option.value || "all"} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
              <Button
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                data-testid="btn-search-training-plan-record"
              >
                查询
              </Button>
            </div>
          </Space>
        </div>

        <TrainingPlanTable
          trainingPlans={trainingPlans}
          loading={loading}
          onViewRecords={handleViewRecords}
          onAddRecord={handleOpenRecordModal}
        />
      </Card>

      <Drawer
        title={drawerTitle}
        open={recordDrawerOpen}
        onClose={handleCloseDrawer}
        width={600}
        destroyOnHidden
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenRecordModal()}
          >
            新增训练记录
          </Button>
        }
      >
        <div className="mb-3 text-gray-500">
          共 {recordPagination.total} 条训练记录
        </div>
        <TrainingRecordTable
          trainingRecords={records}
          loading={recordLoading}
          pagination={{
            current: recordPagination.page,
            pageSize: recordPagination.pageSize,
            total: recordPagination.total,
            onChange: handleRecordPageChange,
          }}
        />
      </Drawer>

      <TrainingRecordModal
        isOpen={recordModalOpen}
        onClose={handleCloseRecordModal}
        onSave={handleSaveRecord}
        trainingRecord={null}
        loading={recordOpLoading}
        trainingPlans={
          activePlan
            ? [
                {
                  id: activePlan.id,
                  name: `${activePlan.studentName || ""} ${activePlan.trainingProjectName || ""}`.trim(),
                },
              ]
            : []
        }
        trainingPlansLoading={false}
        assistTypes={assistTypes.map((type) => ({
          id: type.id,
          name: type.name,
        }))}
        assistTypesLoading={assistTypesLoading}
        users={users.map((user) => ({
          id: user.id,
          name: user.name,
        }))}
        usersLoading={usersLoading}
        initialValues={{
          trainingPlanId: activePlan?.id,
        }}
      />
    </div>
  )
}

export default TrainingPlanRecordManagement
